import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';

import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ChatWidget.css';
import BugReportModal from './BugReportModal';
import LaikaConsentModal from './LaikaConsentModal';
import { useChat } from '../contexts/ChatContext';
import { useLaikaConsent } from '../hooks/useLaikaConsent';
import { useIOSReaderMode } from '../hooks/useIOSReaderMode';
import { getMomentLocaleFromI18nLanguage } from '../utils/momentI18nLocale';
import LaikaAvatar from './LaikaChat/LaikaAvatar';
import LaikaBotMessage from './LaikaChat/LaikaBotMessage';
import LaikaChatHeader from './LaikaChat/LaikaChatHeader';
import LaikaActionPills from './LaikaChat/LaikaActionPills';
import LaikaChatFab from './LaikaChat/LaikaChatFab';
import { shouldShowLaikaFab } from './LaikaChat/laikaFabVisibility';
import { shouldWipeConversationOnDataAccessToggle } from './LaikaChat/laikaConversationContinuity';
import { getLaikaWorkingStatus, localizeLaikaBackendStatus } from './LaikaChat/laikaWorkingStatus';
import type { ActionButton, ChatMessage } from './LaikaChat/types';
import { buildLaikaPageContext } from '../utils/buildLaikaPageContext';
import { HELP_CENTER_URL } from '../config/helpCenter';
import { isValidOnboardingTimestamp } from '../utils/onboardingStorage';
import { resolveLaikaHowToUseMessage } from './LaikaChat/laikaHowToUsePill';
import { trackLaikaMessageSent } from '../analytics';
import { useBoardingModuleVisible } from '../hooks/useBoardingModuleVisible';
import { useProCountryFeatures } from '../hooks/useProCountryFeatures';
import { fetchLaikaReply } from './LaikaChat/fetchAssistantReply';
import { laikaChatErrorI18nKey } from './LaikaChat/laikaChatError';
import { canAccessFollowEngine } from '../constants/followEngineAllowlist';

const ChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation('messaging');
  const { isAuthenticated, user } = useAuth();
  const { isIOSFreeUser } = useIOSReaderMode();
  const boardingVisible = useBoardingModuleVisible();
  const { billingUi } = useProCountryFeatures();
  const location = useLocation();
  const { isChatOpen, openChat, closeChat, dockTarget } = useChat();
  const isOpen = isChatOpen;
  const docked = Boolean(dockTarget);
  const laikaDataAccessEnabled = !!user?.laika_data_access_enabled;
  const pageContext = useMemo(
    () => buildLaikaPageContext(location.pathname, location.search, location.hash),
    [location.pathname, location.search, location.hash],
  );
  const { isEnabled: consentEnabled, acceptedAt, isSubmitting: isConsentSubmitting, updateConsent } = useLaikaConsent();

  // Ancien canary Spoutnik face-à-face : forcer Laika seul.
  useEffect(() => {
    try {
      localStorage.removeItem('rintintin:assistant-agent');
    } catch {
      /* ignore */
    }
  }, []);


  const businessDataKeywords = useMemo(
    () => t('chatWidget.businessDataKeywords', { returnObjects: true }) as string[],
    [t],
  );

  const onboardingCompleted = isValidOnboardingTimestamp(user?.onboarding_completed_at);
  const howToUseMessage = resolveLaikaHowToUseMessage({
    onboardingCompleted,
    newUserMessage: t('chatWidget.actions.howToUse.message'),
    establishedMessage: t('chatWidget.actions.howToUse.establishedMessage'),
  });

  const supportActions = useMemo<ActionButton[]>(() => [
    { id: 'helpCenter', label: t('chatWidget.actions.helpCenter.label'), type: 'link', payload: HELP_CENTER_URL, style: 'default' },
    { id: 'howToUse', label: t('chatWidget.actions.howToUse.label'), message: howToUseMessage, style: 'default' },
    { id: 'findPage', label: t('chatWidget.actions.findPage.label'), message: t('chatWidget.actions.findPage.message'), style: 'default' },
  ], [t, howToUseMessage]);

  const consentAction = useMemo<ActionButton>(() => ({
    id: 'enableDataAccess',
    label: t('chatWidget.actions.enableDataAccess.label'),
    type: 'consent',
    style: 'accent',
  }), [t]);

  const contactHumanAction = useMemo<ActionButton>(() => ({
    id: 'contactHuman',
    label: t('chatWidget.actions.contactHuman.label'),
    type: 'report_bug',
    style: 'default',
  }), [t]);

  const dataActions = useMemo<ActionButton[]>(() => [
    { id: 'nextAppointments', label: t('chatWidget.actions.nextAppointments.label'), message: t('chatWidget.actions.nextAppointments.message'), style: 'default' },
    { id: 'loyalClient', label: t('chatWidget.actions.loyalClient.label'), message: t('chatWidget.actions.loyalClient.message'), style: 'default' },
    { id: 'statistics', label: t('chatWidget.actions.statistics.label'), message: t('chatWidget.actions.statistics.message'), style: 'default' },
    ...supportActions,
  ], [t, supportActions]);

  const getWelcomeActions = useCallback(
    (dataAccessEnabled: boolean): ActionButton[] => (
      dataAccessEnabled ? dataActions : [consentAction, ...supportActions]
    ),
    [consentAction, dataActions, supportActions],
  );

  const createWelcomeMessage = useCallback((dataAccessEnabled: boolean): ChatMessage => ({
    id: '1',
    text: docked ? t('chatWidget.cgvWelcome') : t('chatWidget.welcome'),
    sender: 'bot',
    timestamp: new Date(),
    actions: getWelcomeActions(dataAccessEnabled),
  }), [docked, getWelcomeActions, t]);

  const setIsOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(isChatOpen) : value;
    if (newValue) openChat();
    else closeChat();
  };

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesInitialized, setMessagesInitialized] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [workingStatus, setWorkingStatus] = useState<string | null>(null);
  const [draftText, setDraftText] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [windowSize, setWindowSize] = useState(() => {
    const savedSize = localStorage.getItem('chatWindowSize');
    if (savedSize) {
      return JSON.parse(savedSize);
    }
    return { width: 400, height: 600 };
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => {
    const saved = localStorage.getItem('chatConversationId');
    return saved || null;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const startSizeRef = useRef({ width: 0, height: 0, x: 0, y: 0 });

  useEffect(() => {
    if (messagesInitialized) return;

    const saved = localStorage.getItem('chatMessages');
    if (saved) {
      setMessages(JSON.parse(saved).map((msg: ChatMessage & { timestamp: string }) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
        text: msg.id === '1' && msg.sender === 'bot' ? t('chatWidget.welcome') : msg.text,
        actions: msg.id === '1' && msg.sender === 'bot' ? getWelcomeActions(laikaDataAccessEnabled) : msg.actions,
      })));
    } else {
      setMessages([createWelcomeMessage(laikaDataAccessEnabled)]);
    }
    setMessagesInitialized(true);
  }, [createWelcomeMessage, getWelcomeActions, laikaDataAccessEnabled, messagesInitialized, t]);

  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (!messagesInitialized) return;
    setMessages((prev) => prev.map((msg) => (
      msg.id === '1' && msg.sender === 'bot'
        ? {
            ...msg,
            text: createWelcomeMessage(laikaDataAccessEnabled).text,
            actions: createWelcomeMessage(laikaDataAccessEnabled).actions,
          }
        : msg
    )));
  }, [createWelcomeMessage, i18n.language, laikaDataAccessEnabled, messagesInitialized, howToUseMessage]);

  useEffect(() => {
    if (conversationId) {
      localStorage.setItem('chatConversationId', conversationId);
    } else {
      localStorage.removeItem('chatConversationId');
    }
  }, [conversationId]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const resolved = behavior === 'auto' || behavior === 'smooth' ? behavior : 'smooth';
    messagesEndRef.current?.scrollIntoView({ behavior: resolved });
  }, []);

  useEffect(() => {
    scrollToBottom(draftText ? 'auto' : 'smooth');
  }, [messages, draftText, workingStatus, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 100);
    }
  }, [isOpen, scrollToBottom]);

  useEffect(() => {
    if (user?.user_id) {
      const savedUserId = localStorage.getItem('chatUserId');

      if (savedUserId && savedUserId !== user.user_id.toString()) {
        localStorage.removeItem('chatMessages');
        localStorage.removeItem('chatHasOpenedOnce');
        localStorage.removeItem('chatConversationId');

        setConversationId(null);
        setMessages([createWelcomeMessage(laikaDataAccessEnabled)]);
      }

      localStorage.setItem('chatUserId', user.user_id.toString());
    }
  }, [createWelcomeMessage, laikaDataAccessEnabled, user?.user_id]);

  const handleNewConversation = useCallback(() => {
    localStorage.removeItem('chatMessages');
    localStorage.removeItem('chatConversationId');

    setConversationId(null);
    setMessages([createWelcomeMessage(laikaDataAccessEnabled)]);
  }, [createWelcomeMessage, laikaDataAccessEnabled]);

  const prevLaikaDataAccessRef = useRef(laikaDataAccessEnabled);
  useEffect(() => {
    if (!messagesInitialized) return;
    if (prevLaikaDataAccessRef.current !== laikaDataAccessEnabled) {
      // P1: keep thread on N2 toggle — colleague continuity (no wipe).
      if (shouldWipeConversationOnDataAccessToggle()) {
        handleNewConversation();
      }
    }
    prevLaikaDataAccessRef.current = laikaDataAccessEnabled;
  }, [handleNewConversation, laikaDataAccessEnabled, messagesInitialized]);

  const hasBusinessDataKeywords = useCallback((text: string): boolean => {
    const lower = text.toLowerCase();
    if (!businessDataKeywords.some((kw) => lower.includes(kw))) {
      return false;
    }
    const personalMarkers = ['mes ', 'mon ', 'ma ', 'moi ', 'notre '];
    if (personalMarkers.some((marker) => lower.includes(marker))) {
      return true;
    }
    return /\b(affiche|montre|liste|donne|combien|qui est)\b/.test(lower);
  }, [businessDataKeywords]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startSizeRef.current = {
      width: windowSize.width,
      height: windowSize.height,
      x: e.clientX,
      y: e.clientY,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = startSizeRef.current.x - e.clientX;
      const deltaY = startSizeRef.current.y - e.clientY;

      const newWidth = Math.max(350, Math.min(800, startSizeRef.current.width + deltaX));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 100, startSizeRef.current.height + deltaY));

      setWindowSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'nwse-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'auto';
    };
  }, [isResizing]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    } else {
      setWindowSize({ width: 400, height: 600 });
    }
  };

  const handleConsentUpdate = async (enabled: boolean) => {
    const ok = await updateConsent(enabled);
    if (!ok) return;

    setIsConsentModalOpen(false);
  };

  const closeOnMobile = useCallback(() => {
    if (window.innerWidth < 768) closeChat();
  }, [closeChat]);

  const handleSend = useCallback(async (text: string) => {
    if (!text.trim()) return;

    trackLaikaMessageSent();

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setWorkingStatus(t('chatWidget.workingNow'));
    setDraftText(null);

    if (!laikaDataAccessEnabled && hasBusinessDataKeywords(text)) {
      setIsTyping(false);
      setWorkingStatus(null);
      setDraftText(null);
      const consentPromptMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: t('chatWidget.consentPrompt'),
        sender: 'bot',
        timestamp: new Date(),
        actions: [
          consentAction,
          { id: 'documentation', label: t('chatWidget.actions.documentation.label'), type: 'link', payload: HELP_CENTER_URL, style: 'default' },
        ],
      };
      setMessages((prev) => [...prev, consentPromptMessage]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);
    const fallback = t('chatWidget.fallbackResponse');

    try {
      const reply = await fetchLaikaReply({
        message: text,
        conversationId,
        laikaDataAccessEnabled,
        user,
        pageContext: buildLaikaPageContext(
          location.pathname,
          location.search,
          location.hash,
        ),
        sidebarNav: {
          boardingVisible,
          iosFreeUser: isIOSFreeUser,
          billingUi,
          followEngine: canAccessFollowEngine(user?.email),
        },
        fallbackText: fallback,
        signal: controller.signal,
        onStatus: (status) =>
          setWorkingStatus(status ? localizeLaikaBackendStatus(status, t) : null),
        onPartial: setDraftText,
      });

      clearTimeout(timeoutId);

      if (reply.error) {
        const err = new Error(reply.error === 'timeout' ? 'timeout' : reply.error);
        if (reply.error === 'timeout') err.name = 'AbortError';
        throw err;
      }

      if (reply.conversationId) {
        setConversationId(reply.conversationId);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: reply.text,
          sender: 'bot',
          timestamp: new Date(),
          confidence: reply.confidence,
          sources: reply.sources,
          actions: reply.actions,
        },
      ]);
    } catch (error: any) {
      console.error("Erreur lors de l'envoi du message:", error);
      const errorText = `${t(`chatWidget.${laikaChatErrorI18nKey(error)}`)}\n\n${t('chatWidget.errorPersist')}`;

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: errorText,
          sender: 'bot',
          timestamp: new Date(),
          actions: [contactHumanAction],
        },
      ]);
    } finally {
      clearTimeout(timeoutId);
      setIsTyping(false);
      setWorkingStatus(null);
      setDraftText(null);
    }
  }, [
    consentAction,
    contactHumanAction,
    conversationId,
    hasBusinessDataKeywords,
    laikaDataAccessEnabled,
    pageContext,
    boardingVisible,
    isIOSFreeUser,
    billingUi,
    t,
    user,
    location.hash,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (!isFullscreen) {
      localStorage.setItem('chatWindowSize', JSON.stringify(windowSize));
    }
  }, [isFullscreen, windowSize]);

  const timeLocale = getMomentLocaleFromI18nLanguage(i18n.language);

  if (!isAuthenticated) {
    return null;
  }

  const showFab = shouldShowLaikaFab({
    isAuthenticated,
    isChatOpen: isOpen,
    isIOSFreeUser,
    isDocked: docked,
  });

  const chatPanel = isOpen ? (
          <motion.div
            ref={chatWindowRef}
            initial={docked ? { opacity: 0 } : { scale: 0.92, opacity: 0, y: 16 }}
            animate={docked ? { opacity: 1 } : { scale: 1, opacity: 1, y: 0 }}
            exit={docked ? { opacity: 0 } : { scale: 0.92, opacity: 0, y: 16 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            className={`chat-window-modern${docked ? ' docked' : ''}${!docked && isFullscreen ? ' fullscreen' : ''}${!docked && isResizing ? ' resizing' : ''}`}
            style={!docked && !isFullscreen ? { width: windowSize.width, height: windowSize.height } : undefined}
          >
            <LaikaChatHeader
              isFullscreen={isFullscreen}
              docked={docked}
              onNewConversation={handleNewConversation}
              onReportBug={() => setIsBugModalOpen(true)}
              onOpenSettings={() => setIsConsentModalOpen(true)}
              onToggleFullscreen={toggleFullscreen}
              onClose={() => setIsOpen(false)}
            />

            <div className="chat-messages-modern">
              {messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.03, 0.2) }}
                    className={`chat-message-row ${message.sender}`}
                  >
                    {message.sender === 'bot' && (
                      <LaikaAvatar size={28} className="chat-message-avatar" />
                    )}

                    <div className={`chat-message-wrapper ${message.sender}`}>
                      <div className={`chat-message-modern ${message.sender}`}>
                        {message.sender === 'bot' ? (
                          <LaikaBotMessage
                            text={message.text}
                            sources={message.sources}
                            onInternalLinkClick={closeOnMobile}
                          />
                        ) : (
                          message.text
                        )}
                      </div>

                      <div className="chat-message-time">
                        {message.timestamp.toLocaleTimeString(timeLocale, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </motion.div>

                  {message.actions && message.actions.length > 0 && index === messages.length - 1 && message.sender === 'bot' && (
                    <LaikaActionPills
                      actions={message.actions}
                      onConsent={() => setIsConsentModalOpen(true)}
                      onReportBug={() => setIsBugModalOpen(true)}
                      onCloseMobile={closeOnMobile}
                      onSendMessage={handleSend}
                    />
                  )}
                </React.Fragment>
              ))}

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="chat-message-row bot"
                >
                  <LaikaAvatar size={28} className="chat-message-avatar" />
                  {draftText ? (
                    <div className="chat-message-wrapper bot">
                      <div className="chat-message-modern bot chat-message-draft">
                        <LaikaBotMessage text={draftText} />
                      </div>
                    </div>
                  ) : (
                    <div className="chat-typing">
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: 0 }}
                        className="chat-typing-dot"
                      />
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: 0.1 }}
                        className="chat-typing-dot"
                      />
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: 0.2 }}
                        className="chat-typing-dot"
                      />
                      {getLaikaWorkingStatus(isTyping, workingStatus) && (
                        <span className="chat-typing-status">
                          {getLaikaWorkingStatus(isTyping, workingStatus)}
                        </span>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-modern">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend(inputMessage);
                }}
                className="chat-form chat-input-form"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={
                    docked
                      ? t('chatWidget.cgvInputPlaceholder')
                      : t('chatWidget.inputPlaceholder')
                  }
                  className="chat-input-field"
                />
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  type="submit"
                  className="chat-send-button-modern"
                  disabled={!inputMessage.trim() || isTyping}
                  aria-label={t('chatWidget.sendAriaLabel')}
                >
                  <Send size={18} strokeWidth={2.25} aria-hidden />
                </motion.button>
              </form>
            </div>

            {!isFullscreen && !docked && (
              <div
                className="chat-resize-handle"
                onMouseDown={handleMouseDown}
                title={t('chatWidget.resize')}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
                  <path d="M1,11 L11,1 M4,11 L11,4 M7,11 L11,7" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </div>
            )}
          </motion.div>
  ) : docked ? (
    <button
      type="button"
      className="cgv-laika-dock__idle"
      onClick={() => openChat('cgv_dock')}
    >
      <LaikaAvatar size={40} showStatus />
      <span className="cgv-laika-dock__idle-copy">
        <strong>{t('chatWidget.assistantName')}</strong>
        <span>{t('chatWidget.cgvDockIdle')}</span>
      </span>
    </button>
  ) : null;

  return (
    <>
      <AnimatePresence>
        {showFab && (
          <LaikaChatFab
            key="laika-fab"
            onOpen={() => openChat('fab')}
            ariaLabel={t('chatWidget.openAriaLabel')}
          />
        )}
      </AnimatePresence>
      {docked && dockTarget
        ? createPortal(chatPanel, dockTarget)
        : (
          <AnimatePresence>
            {isOpen ? chatPanel : null}
          </AnimatePresence>
        )}
      <BugReportModal isOpen={isBugModalOpen} onClose={() => setIsBugModalOpen(false)} />
      <LaikaConsentModal
        isOpen={isConsentModalOpen}
        isEnabled={consentEnabled}
        submitting={isConsentSubmitting}
        acceptedAt={acceptedAt}
        onClose={() => setIsConsentModalOpen(false)}
        onEnable={() => handleConsentUpdate(true)}
        onDisable={() => handleConsentUpdate(false)}
      />
    </>
  );
};

export default ChatWidget;
